import React, { useState } from "react";
import { Sparkles, Loader2, MessageCircle, Copy, RefreshCcw, Check, Lock } from "lucide-react";
import { endpoints } from "../lib/api";
import { openWhatsApp } from "../lib/wa";
import { isAIEnabled, planLockReason } from "../lib/plan";
import { toast } from "sonner";

const TONES = [
  { key: "friendly", label: "Friendly" },
  { key: "urgent", label: "Urgent" },
  { key: "professional", label: "Professional" },
];
const LANGS = [
  { key: "hinglish", label: "Hinglish" },
  { key: "en", label: "English" },
  { key: "hi", label: "हिंदी" },
];

const KIND_LABEL = {
  lead_followup: "Lead follow-up",
  trial_close: "Trial close",
  retention: "Re-engagement",
  renewal: "Renewal nudge",
};

/**
 * Reusable AI message suggester.
 * props:
 *  - kind: "lead_followup" | "trial_close" | "retention" | "renewal"
 *  - targetKind: "lead" | "member"
 *  - targetId: string
 *  - phone: string (e.g. "+9198..." used by WhatsApp send)
 *  - defaultLang?: "hinglish" | "en" | "hi"
 *  - className?: string
 */
export default function AISuggestPanel({
  kind = "lead_followup",
  targetKind = "lead",
  targetId,
  phone,
  defaultLang = "hinglish",
  className = "",
}) {
  const [tone, setTone] = useState("friendly");
  const [lang, setLang] = useState(defaultLang);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(0);
  const [draft, setDraft] = useState("");
  const [copiedFlash, setCopiedFlash] = useState(false);

  const canSend = !!phone && draft.trim().length > 0;
  const aiEnabled = isAIEnabled();
  const lockReason = planLockReason();

  const generate = async () => {
    if (!aiEnabled) return;
    if (!targetId || loading) return;
    setLoading(true);
    setSuggestions([]);
    setDraft("");
    try {
      const { data } = await endpoints.aiSuggest({
        kind, target_kind: targetKind, target_id: targetId,
        tone, lang, n: 2,
      });
      const arr = data?.suggestions || [];
      if (arr.length === 0) {
        toast.error("No suggestions returned. Try again.");
        return;
      }
      setSuggestions(arr);
      setSelected(0);
      setDraft(arr[0]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "AI service unavailable";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const pick = (i) => {
    setSelected(i);
    setDraft(suggestions[i] || "");
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopiedFlash(true);
      setTimeout(() => setCopiedFlash(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const send = () => {
    if (!canSend) return;
    openWhatsApp(phone, draft);
  };

  return (
    <div className={`border border-border rounded-xl p-5 bg-card ${className}`} data-testid="ai-suggest-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-white"
            style={{ background: "linear-gradient(135deg, #7C3AED 0%, #DB2777 50%, #F97316 100%)" }}
            aria-hidden
          >
            <Sparkles className="w-4 h-4" />
          </span>
          <div>
            <p className="font-display text-sm font-semibold text-foreground leading-none">
              AI {KIND_LABEL[kind] || "Suggestion"}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Smart helper · review before sending
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={!targetId || loading || !aiEnabled}
          data-testid="ai-suggest-generate"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white transition-opacity disabled:opacity-60"
          style={{ background: "linear-gradient(135deg, #7C3AED 0%, #DB2777 60%, #F97316 100%)" }}
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : aiEnabled ? <Sparkles className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
          {!aiEnabled ? "AI Plan" : suggestions.length === 0 ? "Generate" : "Regenerate"}
        </button>
      </div>

      {!aiEnabled ? (
        <div
          data-testid="ai-locked"
          className="rounded-lg border border-dashed border-purple-300/50 dark:border-purple-700/40 p-4 text-center"
        >
          <Lock className="w-4 h-4 mx-auto mb-2 text-purple-500" />
          <p className="text-sm font-medium text-foreground mb-1">AI message suggestions</p>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">{lockReason}</p>
        </div>
      ) : (
        <>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Tone</p>
          <div className="flex gap-1 bg-muted/40 p-1 rounded-md w-full" data-testid="ai-tone-picker">
            {TONES.map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTone(t.key)}
                data-testid={`ai-tone-${t.key}`}
                className={`flex-1 text-[11px] font-medium px-2 py-1 rounded transition-colors ${
                  tone === t.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Language</p>
          <div className="flex gap-1 bg-muted/40 p-1 rounded-md w-full" data-testid="ai-lang-picker">
            {LANGS.map(l => (
              <button
                key={l.key}
                type="button"
                onClick={() => setLang(l.key)}
                data-testid={`ai-lang-${l.key}`}
                className={`flex-1 text-[11px] font-medium px-2 py-1 rounded transition-colors ${
                  lang === l.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {suggestions.length > 1 && (
        <div className="flex gap-1 mb-2" data-testid="ai-variants">
          {suggestions.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => pick(i)}
              data-testid={`ai-variant-${i}`}
              className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded transition-colors ${
                selected === i ? "bg-foreground text-background" : "bg-muted/50 text-muted-foreground hover:bg-muted"
              }`}
            >
              Variant {i + 1}
            </button>
          ))}
        </div>
      )}

      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={loading ? "Generating…" : "Click Generate to draft a smart message…"}
        rows={4}
        data-testid="ai-draft-textarea"
        className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 resize-none placeholder:text-muted-foreground/60"
      />

      <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
        <div className="text-[11px] text-muted-foreground/70">
          {draft.length}/280 chars
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={generate}
            disabled={loading || suggestions.length === 0}
            data-testid="ai-regen-btn"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"
            title="Regenerate"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            type="button"
            onClick={copyToClipboard}
            disabled={!draft}
            data-testid="ai-copy-btn"
            className="inline-flex items-center gap-1 text-xs bg-card border border-border hover:bg-muted/50 text-foreground px-2.5 py-1.5 rounded-md disabled:opacity-40 transition-colors"
          >
            {copiedFlash ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            {copiedFlash ? "Copied" : "Copy"}
          </button>
          <button
            type="button"
            onClick={send}
            disabled={!canSend}
            data-testid="ai-send-wa-btn"
            className="inline-flex items-center gap-1.5 text-xs bg-[#25D366] hover:bg-[#1EBE5D] text-white font-semibold px-3 py-1.5 rounded-md disabled:opacity-40 transition-colors"
          >
            <MessageCircle className="w-3.5 h-3.5" /> Send via WhatsApp
          </button>
        </div>
      </div>
        </>
      )}
    </div>
  );
}
